<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import TableHeader from '@/components/TableHeader.vue'
import AddStudentsSidePanel from '@/components/SidePanels/AddStudents.vue'
import { studentModule } from '@/store/student/module'
import { statsModule } from '@/store/stats/module'
import { groupModule } from '@/store/group/module'
import UIKit, {
    type ITableSortSettings,
    type ITableAction,
    type ITableColumn,
    type TTableRow,
    type TTableSubrow,
} from '@pocketprep/ui-kit'
import { isPromise } from '@/utils'
import { licenseModule } from '@/store/license/module'
import { organizationModule } from '@/store/organization/module'
import { analyticsModule } from '@/store/analytics/module'
import { examModule } from '@/store/exam/module'
import PurchaseSeatsModal from '@/components/SeatsModal/PurchaseSeatsModal.vue'

const {
    Table,
    TableActions,
    Search,
    FilterOptions,
    Button: PocketButton,
    SkeletonLoader,
    Tag,
    EmptyState,
    Pill,
} = UIKit

export type TStudent = {
    id: string
    studentNameOrEmail: string
    averageScore: string
    pctAnswered: string
    sortName: string
    selected: boolean
    isLoading?: boolean
    isPending?: boolean
    styles?: Record<string, string>
}

type TStudentStatusOptions = {
    id: string
    label: string
    enabled: boolean
}

const route = useRoute()
const router = useRouter()

const table = ref<HTMLElement | null>(null)
const searchString = ref('')
const showAddStudentSidePanel = ref(false)
const showFilterDropdown = ref(false)
const isLoading = ref(false)
const rowHoverId = ref<string | null>(null)
const currentGroupId = ref<string | null>(null)
const showPurchaseSeatsModal = ref(false)

const checkboxOptions = ref<TStudentStatusOptions[]>([
    { id: 'active', label: 'Active', enabled: false },
    { id: 'pending', label: 'Pending', enabled: false },
])

const studentActions: ITableAction[] = [
    { id: 1, icon: 'filter', text: 'Filter Students' },
    { id: 2, icon: 'add', text: 'Add Students' },
]

const studentColumns: ITableColumn[] = [{
    name: 'Student',
    propName: 'studentNameOrEmail',
    styles: { width: '177px' },
}, {
    name: 'Average',
    propName: 'averageScore',
    styles: { width: '64px', textAlign: 'right' },
}, {
    name: 'Answered',
    propName: 'pctAnswered',
    styles: { width: '72px', textAlign: 'right' },
}]

const sortSettings = ref<ITableSortSettings>({ column: studentColumns[0], direction: 1 })

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const showPurchaseSeatsButton = computed(() => {
    return organizationModule.getters.getHasSelfServicePaymentAccess()
})

const currentStudentId = computed(() => {
    return studentModule.state.currentStudentId
})

const currentOrgStudentUsers = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUsers()
})

const currentGroup = computed(() => {
    return groupModule.getters.getCurrentGroup()
})

const areAllOrgExamsFree = computed(() => {
    return examModule.getters.getAreAllOrgExamsFree()
})

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const currentOrgPendingLicenses = computed(() => {
    return currentOrgLicenses.value.filter(l => l.status === 'pending')
})

const studentAggStats = computed(() => {
    return statsModule.getters.getStudentAggStats()
})

const currentGroupStudents = computed(() => {
    return studentModule.getters.getCurrentGroupStudents()
})

const mappedStudents = computed((): TStudent[] => {
    const studentStatsVal = studentAggStats.value
    return currentGroupStudents.value.map(student => {
        const stats = studentStatsVal[student.objectId]
        if (student.user) {
            const studentUser = currentOrgStudentUsers.value[student.user.objectId]
            const name = student.displayFirstName || student.displayLastName
                ? [ student.displayFirstName, student.displayLastName ].join(' ')
                : studentUser
                    ? studentUser.firstName || studentUser.lastName
                        ? [ studentUser.firstName, studentUser.lastName ].join(' ')
                        : studentUser.username
                    : ''
            const sortName = student.displayFirstName || student.displayLastName
                ? [ student.displayLastName, student.displayFirstName ].join(' ')
                : studentUser
                    ? studentUser.firstName || studentUser.lastName
                        ? [ studentUser.lastName, studentUser.firstName ].join(' ')
                        : studentUser.username
                    : ''

            const mappedStudent: TStudent = {
                id: student.objectId,
                studentNameOrEmail: name,
                isLoading: !stats || isPromise(stats) || stats.isLoading,
                isPending: false,
                averageScore: !stats || isPromise(stats) || stats.isLoading || stats.totalAnswers === 0
                    ? '--'
                    : `${Math.round((stats.correctAnswerCount / stats.totalAnswers) * 100)}%`,
                pctAnswered: !stats || isPromise(stats) || stats.isLoading || stats.totalQuestions === 0
                    ? '--'
                    : `${Math.round((stats.totalAnswers / (stats.totalQuestions || Infinity)) * 100)}%`,
                sortName,
                selected: false,
            }

            if (mappedStudent.id === currentStudentId.value) {
                mappedStudent.selected = true
                mappedStudent.styles = {
                    backgroundColor: 'rgba(255, 249, 239)',
                    fontWeight: '600',
                }
            } else if (mappedStudent.id === rowHoverId.value) {
                mappedStudent.styles = {
                    backgroundColor: `${BrandColors.grayBackground}60`,
                }
            }
            return mappedStudent
        } else {
            const user = currentOrgPendingLicenses.value.find(opl => opl.orgStudent?.objectId === student.objectId)
            const pendingStudent = {
                id: student.objectId,
                studentNameOrEmail: user?.userEmail || '',
                sortName: user?.userEmail || '',
                isPending: true,
                averageScore: '--',
                pctAnswered: '--',
                styles: {
                    color: BrandColors.ash,
                },
                selected: false,
            } as TStudent

            if (pendingStudent.id === currentStudentId.value) {
                pendingStudent.selected = true
                pendingStudent.styles = {
                    backgroundColor: 'rgba(255, 249, 239)',
                    fontWeight: '600',
                }
            } else if (pendingStudent.id === rowHoverId.value) {
                pendingStudent.styles = {
                    backgroundColor: `${BrandColors.grayBackground}60`,
                }
            }

            return pendingStudent
        }
    })
})

const enabledFilterStatuses = computed(() => {
    return checkboxOptions.value.reduce<string[]>((acc, s) => {
        if (s.enabled) {
            const first = s.label.split(' ')[0]
            if (first !== undefined) {
                acc.push(first)
            }
        }
        return acc
    }, [])
})

const filteredStudents = computed(() => {
    const groupStudents = currentGroup.value?.orgStudents || []
    const searchVal = searchString.value
    return groupStudents.reduce((acc, gs) => {
        const mappedGroupStudent = mappedStudents.value.find(ms => ms.id === gs.objectId)
        if (mappedGroupStudent) {
            const studentStatus = mappedGroupStudent.isPending ? 'Pending' : 'Active'
            const matchesSearch = !searchVal
                || mappedGroupStudent.studentNameOrEmail.toLowerCase().includes(searchVal.toLowerCase())
            const matchesFilter = enabledFilterStatuses.value.length
                && enabledFilterStatuses.value.includes(studentStatus) || !enabledFilterStatuses.value.length
            if (matchesSearch && matchesFilter) {
                acc.push(mappedGroupStudent)
            }
        }
        return acc
    }, [] as TStudent[])
})

const activeCountForGroup = computed(() => {
    return filteredStudents.value.filter(fs => fs && !fs.isPending).length
})

const pendingCountForGroup = computed(() => {
    return filteredStudents.value.filter(fs => fs?.isPending).length
})

const groupFilters = computed(() => {
    const cg = currentGroup.value
    return cg?.name
        ? [
            { id: cg.objectId, label: cg.name, showClose: false },
            ...enabledFilterStatuses.value.map(efs => ({
                id: efs.toLocaleLowerCase(),
                label: efs,
                showClose: true,
            })),
        ]
        : []
})

const visibleStudentActions = computed(() => {
    return (currentOrgLicenses.value.length || areAllOrgExamsFree.value)
        ? studentActions
        : studentActions.filter(sa => sa.icon !== 'add')
})

const filteredSortedStudents = computed(() => {
    const sortSettingsVal = sortSettings.value
    const students = [ ...filteredStudents.value ]
    return students.sort((a, b) => {
        if (sortSettingsVal?.column) {
            if (sortSettingsVal.column.name === 'Student') {
                return sortSettingsVal.direction * a.sortName.localeCompare(b.sortName)
            } else if (sortSettingsVal.column.name === 'Average') {
                return sortSettingsVal.direction
                    * b.averageScore.localeCompare(a.averageScore, undefined, { numeric: true })
            } else if (sortSettingsVal.column.name === 'Answered') {
                return sortSettingsVal.direction
                    * b.pctAnswered.localeCompare(a.pctAnswered, undefined, { numeric: true })
            }
        }
        return -1
    })
})

const emptyGroupEmptyStateTitle = computed(() => {
    return 'There\'s nobody here yet'
})

const emptyStateTitle = computed(() => {
    return searchString.value ? `No results for "${searchString.value}"` : 'No results'
})

const isFilterEnabled = (status: string) => {
    return !!checkboxOptions.value.find(s => s.id === status && s.enabled === true)
}

const removeFilter = (filterId: string) => {
    checkboxOptions.value = checkboxOptions.value.map(o => o.id === filterId ? { ...o, enabled: false } : o)
}

const removeFilters = () => {
    checkboxOptions.value = checkboxOptions.value.map(o => ({ ...o, enabled: false }))
}

const setFilterOptions = (enabledFilterOption?: string | null) => {
    checkboxOptions.value = [
        {
            id: 'active',
            label: `Active (${activeCountForGroup.value})`,
            enabled: isFilterEnabled('active') || enabledFilterOption === 'active',
        },
        {
            id: 'pending',
            label: `Pending (${pendingCountForGroup.value})`,
            enabled: isFilterEnabled('pending') || enabledFilterOption === 'pending',
        },
    ]
}

const closeAddStudentSidePanel = () => {
    showAddStudentSidePanel.value = false
}

const closePurchaseSeatsModal = () => {
    showPurchaseSeatsModal.value = false
}

const openPurchaseSeatsModal = () => {
    showPurchaseSeatsModal.value = true
    if (currentOrganization.value) {
        analyticsModule.actions.amplitudeTrack('Side Panel Opened', {
            sidePanelName: 'Purchase Seats',
            sidePanelPlacement: 'Stats Students Table',
            organizationId: currentOrganization.value.objectId,
            organizationName: currentOrganization.value.name,
        })
    }
}

const openAddStudentSidePanel = () => {
    showAddStudentSidePanel.value = true
}

const clickListener = (e: MouseEvent) => {
    const targetEl = e.target as HTMLElement
    const tableEl = table.value
    if (!tableEl?.contains(targetEl) && !targetEl?.closest('.uikit-table-actions__action-icon')
        && document.body.contains(targetEl)) {
        showFilterDropdown.value = false
    }
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = String(row.id)
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

const rowClicked = async (row: TTableSubrow | TStudent) => {
    if (!('studentNameOrEmail' in row)) {
        return
    }
    const student = row as TStudent
    const currentGroupVal = currentGroup.value
    if (currentGroupVal
        && (route.params.groupId !== currentGroupVal.objectId || route.params.studentId !== student.id)) {
        const routeName = route.name as string
        if (routeName.includes('stats')) {
            await router.push({
                name: 'student-stats',
                params: {
                    groupId: currentGroupVal.objectId,
                    studentId: student.id,
                },
                ...(student.isPending && { query: { isPending: 'true' } }),
            })
        } else if (routeName.includes('content')) {
            await router.push({
                name: 'student-content',
                params: {
                    groupId: currentGroupVal.objectId,
                    studentId: student.id,
                },
                ...(student.isPending && { query: { isPending: 'true' } }),
            })
        }
    }
    analyticsModule.actions.amplitudeTrack('Stats Student Table Clicked', {
        studentName: student.studentNameOrEmail,
        organizationName: currentOrganization.value?.name || '',
    })
}

const studentActionClicked = (action: { text: string }) => {
    if (action.text === 'Add Students') {
        showAddStudentSidePanel.value = true
    }
    if (action.text === 'Filter Students') {
        showFilterDropdown.value = !showFilterDropdown.value
    }
}

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    sortSettings.value = newSortSettings
}

const scrollStudentRows = () => {
    if (filteredSortedStudents.value?.length) {
        const selectedStudentRowIndex = filteredSortedStudents.value.findIndex(studentRow => studentRow.selected)

        nextTick(() => {
            const studentRowEls = document.querySelectorAll(
                '.students-table tbody.uikit-table__rows .uikit-table__row'
            )
            const selectedStudentRowEl = studentRowEls?.[selectedStudentRowIndex] as HTMLElement | undefined
            const selectedRowOffsetTop = selectedStudentRowEl?.offsetTop

            const studentRowsContainerEl = document.querySelector(
                '.students-table tbody.uikit-table__rows'
            ) as HTMLElement | undefined
            if (
                studentRowsContainerEl
                && selectedRowOffsetTop
                && selectedRowOffsetTop > studentRowsContainerEl.offsetHeight
            ) {
                studentRowsContainerEl.scrollTop = selectedRowOffsetTop || 0
            }
        })
    }
}

onMounted(async () => {
    isLoading.value = true

    currentGroupId.value = route.params.groupId as string

    document.addEventListener('click', clickListener)

    sortSettings.value = {
        column: studentColumns[0],
        direction: 1,
    }

    await Promise.all([
        examModule.actions.fetchExamMetadata(),
        studentModule.actions.fetchCurrentOrgStudents(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        groupModule.actions.fetchCurrentGroup(),
        statsModule.actions.fetchCurrentStudentStats(),
        statsModule.actions.fetchCurrentGroupAggStats(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])

    setFilterOptions()

    if (currentStudentId.value) {
        const foundStudent = filteredSortedStudents.value.find(student => student.id === currentStudentId.value)
        if (foundStudent) {
            await rowClicked(foundStudent)
            scrollStudentRows()
        }
    }

    isLoading.value = false
})

onBeforeUnmount(() => {
    document.removeEventListener('click', clickListener)
})

watch(currentGroup, () => {
    removeFilters()
})

watch(currentGroup, () => {
    setFilterOptions()
})
</script>

<template>
    <div
        ref="table"
        class="students-table"
    >
        <AddStudentsSidePanel
            v-if="showAddStudentSidePanel"
            @close="closeAddStudentSidePanel"
        />
        <PurchaseSeatsModal
            v-if="showPurchaseSeatsModal"
            @close="closePurchaseSeatsModal"
        />
        <TableHeader
            title="Students"
            :count="filteredSortedStudents.length"
        >
            <template #tableHeaderRight>
                <TableActions
                    :actions="visibleStudentActions"
                    @actionClicked="studentActionClicked"
                />
            </template>

        </TableHeader>
        <div
            class="students-table__filter-container"
        >
            <FilterOptions
                v-show="showFilterDropdown"
                v-model="checkboxOptions"
                class="students-table__filter"
                :class="{
                    'students-table__filter--no-add': visibleStudentActions.length === 1
                }"
                filter-options-label="FILTER BY STATUS"
                :checkboxEntries="checkboxOptions"
                :radioEntries="[]"
            />
        </div>
        <Search
            v-model="searchString"
            type="white"
            placeholder="Search"
            @input="setFilterOptions"
        />
        <div class="students-table__filters">
            <span class="students-table__filters-label">Filters:</span>
            <Pill
                v-for="filter in groupFilters"
                :key="filter.id"
                :show-close="filter.showClose"
                class="students-table__filter-chip"
                @click="removeFilter(filter.id)"
            >
                {{ filter.label }}
            </Pill>
        </div>
        <Table
            class="students-table__table"
            :show-header="false"
            :rows="filteredSortedStudents"
            :columns="studentColumns"
            :default-sort="sortSettings"
            :sort-toggle-order="[ 1, -1 ]"
            :table-container-styles="{
                borderRadius: '0 0 4px 4px',
            }"
            :table-content-styles="{
                borderWidth: '0 1px 1px 1px',
                borderColor: '#dfe3e7',
                borderStyle: 'solid',
                borderRadius: '0 0 4px 4px',
            }"
            :table-column-labels-styles="{
                backgroundColor: '#f4f5f9',
                padding: '3px 0 3px 15px',
            }"
            :table-column-label-styles="{
                padding: '0',
                margin: '0',
            }"
            :table-rows-styles="{
                maxHeight: '414px',
            }"
            :table-row-styles="{
                fontSize: '13px',
                cursor: 'pointer',
                height: '38px',
                padding: '9px 0 10px 15px',
            }"
            :table-sort-icon-styles="{
                marginLeft: '2px',
                marginRight: '2px'
            }"
            :table-cell-styles="{
                margin: '0',
                padding: '0',
            }"
            @sort="updateSortSettings"
            @rowEnter="rowEnter"
            @rowLeave="rowLeave"
            @rowClicked="rowClicked"
        >
            <template #tableCellValue="{ row, column }">
                <SkeletonLoader
                    v-if="column.propName === 'averageScore' && row.isLoading"
                    class="students-table__loader-avg"
                />
                <SkeletonLoader
                    v-if="column.propName === 'pctAnswered' && row.isLoading"
                    class="students-table__loader-ans"
                />
                <Tag
                    v-if="column.propName === 'pctAnswered' && row.isPending"
                    :isPendingTag="true"
                    size="small"
                    class="students-table__status-pill"
                >
                    <template #tagMessage>
                        Pending
                    </template>
                </Tag>
            </template>
        </Table>
        <div
            v-if="!isLoading &&
                currentGroup &&
                filteredSortedStudents.length === 0 &&
                groupFilters.length === 1 &&
                !currentOrgLicenses.length &&
                !areAllOrgExamsFree &&
                !searchString"
            class="students-table__call-to-action-container"
        >
            <div
                v-for="n in 12"
                :key="n"
                class="students-table__empty-row"
            />
            <div class="students-table__call-to-action-info">
                <div class="students-table__better-with-students">
                    Better With Students
                </div>
                <div class="students-table__blobs">
                    <img
                        class="students-table__phone-hand"
                        src="@/assets/images/student-list-blank-top.png"
                    >
                    <div class="students-table__left-text">
                        Students study by taking practice quizzes
                    </div>
                    <img
                        class="students-table__chart-bars"
                        src="@/assets/images/student-list-blank-bottom.png"
                    >
                    <div class="students-table__right-text">
                        You see their progress and where they need help
                    </div>
                </div>
                <PocketButton
                    v-if="showPurchaseSeatsButton"
                    class="students-table__purchase-seats-btn"
                    @click="openPurchaseSeatsModal"
                    @mousedown.prevent
                >
                    Purchase Seats
                </PocketButton>
            </div>
        </div>
        <div
            class="students-table__filter-no-results"
            v-else-if="!isLoading &&
                currentGroup &&
                filteredSortedStudents.length === 0 &&
                groupFilters.length === 1 &&
                !searchString && (
                    currentOrgLicenses.length ||
                    areAllOrgExamsFree
                )"
        >
            <EmptyState
                class="students-table__empty-state"
                :title="emptyGroupEmptyStateTitle"
                message="Get started by adding some students to this group."
            >
                <template #imageRow>
                    <div />
                </template>
            </EmptyState>
            <PocketButton
                class="students-table__add-students-btn"
                @click="openAddStudentSidePanel"
                @mousedown.prevent
            >
                Add Students
            </PocketButton>
        </div>
        <div
            class="students-table__filter-no-results"
            v-else-if="filteredSortedStudents.length === 0 && (groupFilters.length > 1 || searchString)"
        >
            <EmptyState
                class="students-table__empty-state"
                :title="emptyStateTitle"
                message="There are no results that match your filter and search criteria."
            >
                <template #imageRow>
                    <div />
                </template>
            </EmptyState>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.students-table {
    &__filters {
        display: flex;
        padding: 8px 22px 8px 16px;
        font-size: 13px;
        line-height: 16px;
        align-items: center;
        background-color: white;
        border-width: 0 1px;
        border-style: solid;
        border-color: #dfe3e7;
    }

    &__filter-container {
        position: relative;
        z-index: 2;
    }

    &__filter {
        width: 240px;
        position: absolute;
        left: 40px;
        top: 10px;

        &--no-add {
            left: 64px;
        }
    }

    &__filters-label {
        padding: 3px 0;
        margin-right: 8px;
    }

    &__filter-chip {
        margin-top: 3px;
    }

    &__loader-avg,
    &__loader-ans {
        height: 14px;
        width: 48px;
    }

    &__loader-avg {
        margin: 2.5px 0 0 5px;
    }

    &__loader-ans {
        margin: 2.5px 0 0 8px;
        padding-right: 17px;
    }

    &__status-pill {
        margin-right: 17px;
    }

    &__call-to-action-container {
        margin-top: -4px;
        position: relative;
        height: 435px;
        overflow: hidden;
        border: 1px solid $gray-divider;
        border-radius: 0 0 4px 4px;
    }

    &__empty-row {
        height: 38px;
        background-color: white;
        box-shadow: inset 0 -1px 0 0 $gray-divider;
    }

    &__filter-no-results {
        height: 456px;
        background-color: white;
        border: 1px solid $gray-divider;
        border-radius: 0 0 4px 4px;
        text-align: center;
    }

    &__add-students-btn {
        margin-top: 20px;
    }

    &__call-to-action-info {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: absolute;
        top: 55px;
        left: 46px;
        width: 230px;
        height: 305px;
        background-color: white;
    }

    &__better-with-students {
        font-weight: 600;
        font-size: 15px;
        line-height: 20px;
        padding: 8px;
    }

    &__blobs {
        position: relative;
        width: 100%;
        height: 245px;
        font-size: 13px;
        line-height: 16px;
        color: $slate-01;
    }

    &__phone-hand {
        position: absolute;
        top: 18px;
        right: 24px;
        width: 77px;
    }

    &__left-text {
        position: absolute;
        top: 36px;
        left: 24px;
        width: 103px;
    }

    &__chart-bars {
        position: absolute;
        left: 24px;
        bottom: 48px;
        width: 98px;
    }

    &__right-text {
        position: absolute;
        right: 24px;
        bottom: 30px;
        width: 123px;
        text-align: right;
    }

    &__empty-state {
        margin: 0 auto;
        padding-top: 40px;
        max-width: 100%;
    }

    :deep(*) {
        .uikit-empty-state__message {
            padding: 0;
        }
        .uikit-search__icon {
            left: 16px;
        }

        .uikit-search__input {
            padding: 10px 44px 10px 39px;
            font-size: 13px;
            border-radius: 4px 4px 0 0;
        }

        .uikit-table__row:last-child {
            border-bottom: none;
        }

        .uikit-overflow-tooltip__content {
            padding-right: 17px;
        }
    }
}
</style>
